import { Box, Button, CircularProgress, Stack, TextField, Typography } from "@mui/material";
import * as React from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useIdentity } from "../../containers/IdentityContainer";
import { createUnauthenticatedPostgrest } from "../../utils/unauthenticatedApi";
import { useTokenMaker } from "../../hooks";
const logo = "/airplx-logo-transparent-square-no-text.png";

export const Signup: React.FC<{}> = () => {
  const { isLoggedIn } = useIdentity();
  const location = useLocation();
  const navigate = useNavigate();
  const { url: tokenMakerURL } = useTokenMaker();

  const [email, setEmail] = React.useState<string>(location.state?.email || "");
  const [password, setPassword] = React.useState<string>("");
  const [confirmPassword, setConfirmPassword] = React.useState<string>("");
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string>("");

  const postgrest = React.useMemo(() => {
    return createUnauthenticatedPostgrest();
  }, []);

  if (isLoggedIn) {
    return <Navigate to="/" replace />;
  }

  const validateEmail = (): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address");
      return false;
    }
    return true;
  };

  const validatePassword = (): boolean => {
    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return false;
    }
    if (!/[A-Z]/.test(password)) {
      setError("Password must contain at least one uppercase letter");
      return false;
    }
    if (!/[a-z]/.test(password)) {
      setError("Password must contain at least one lowercase letter");
      return false;
    }
    if (!/[0-9]/.test(password)) {
      setError("Password must contain at least one number");
      return false;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return false;
    }
    return true;
  };

  const handleSignup = async () => {
    setError("");

    if (!validateEmail() || !validatePassword()) {
      return;
    }

    setLoading(true);

    try {
      // Register user (creates unverified user, returns email)
      const { data: registeredEmail, error: rpcError } = await postgrest.rpc("register_with_password", {
        p_email: email,
        p_password: password,
      });

      if (rpcError) {
        setError(rpcError.message || "Failed to create account");
        setLoading(false);
        return;
      }

      if (!registeredEmail) {
        setError("Failed to create account. Please try again.");
        setLoading(false);
        return;
      }

      // Send verification email
      await fetch(`${tokenMakerURL}/send-verification-email`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      // Redirect to verification page
      navigate("/verify-email", { state: { email } });
    } catch (err: any) {
      setError(err.message || "An error occurred. Please try again.");
      console.error("Error signing up:", err);
      setLoading(false);
    }
  };

  const getPasswordStrength = (): string => {
    if (!password) return "";
    if (password.length < 8) return "Too short";

    let strength = 0;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;

    if (strength === 4 && password.length >= 12) return "Strong";
    if (strength >= 3) return "Good";
    if (strength >= 2) return "Fair";
    return "Weak";
  };

  const passwordStrength = getPasswordStrength();

  return (
    <>
      <Stack
        direction="column"
        justifyContent="center"
        sx={{ height: "100%", width: "100%" }}
      >
        <Box
          sx={{
            width: 368,
            margin: "auto",
            border: "2px solid #E2E2E2",
            p: 3,
            mt: 20,
          }}
        >
          <Stack
            direction="column"
            alignItems="center"
            justifyContent="center"
            spacing={1}
          >
            <Typography align="center" variant="h4">
              Create Account
            </Typography>
            <Typography align="center" variant="subtitle1" color="text.secondary">
              Sign up to get started
            </Typography>
          </Stack>
          <br />
          <br />

          <Stack direction="column" spacing={2}>
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
              autoFocus
              disabled={loading}
            />

            <TextField
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              disabled={loading}
            />

            {password && (
              <Typography
                variant="body2"
                sx={{ mt: -1 }}
                color={
                  passwordStrength === "Strong" ? "success.main" :
                  passwordStrength === "Good" ? "primary.main" :
                  passwordStrength === "Fair" ? "warning.main" :
                  "error.main"
                }
              >
                Strength: {passwordStrength}
              </Typography>
            )}

            <TextField
              label="Confirm Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleSignup()}
              fullWidth
              error={Boolean(error)}
              helperText={error}
              disabled={loading}
            />

            <Typography variant="caption" color="text.secondary">
              Must be 8+ characters with uppercase, lowercase, and numbers
            </Typography>

            <Button
              variant="contained"
              onClick={handleSignup}
              fullWidth
              disabled={loading || !email || !password || !confirmPassword}
              sx={{ borderRadius: 1 }}
            >
              {loading ? <CircularProgress size={24} /> : "Sign Up"}
            </Button>

            <Typography variant="body2" align="center" color="text.secondary">
              Already have an account?{" "}
              <Link to="/login" style={{ textDecoration: "none", color: "#0066cc" }}>
                Log in
              </Link>
            </Typography>
          </Stack>
        </Box>
      </Stack>
      <img
        src={logo}
        style={{ position: "fixed", bottom: 15, right: 15, width: 50 }}
      />
    </>
  );
};
